import React from "react";
import { Globe, AlertTriangle, CheckCircle2, TrendingDown } from "lucide-react";
import { formatEUR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const PRICE_CHECK_MIN = 250;
export const PRICE_CHECK_TOLERANCE = 0.15;
const VAT = 1.21;
export const REF_SOURCES = ["be_webshop", "nl_webshop", "manufacturer", "other"];

/* Marktcheck: Belgische marktprijs (standaard incl. btw) vergelijken met eigen verkoopprijs excl. btw.
   Venster: markt ≤ prijs ≤ markt + 15 %. Verplicht boven € 250. */
export function PriceCheck({ cost, price, reference, inclVat = true, source = "be_webshop", onReference, onInclVat, onSource, t, idPrefix = "price-check" }) {
  const c = Number(cost) || 0, p = Number(price) || 0, raw = Number(reference) || 0;
  const refExcl = raw > 0 ? (inclVat ? raw / VAT : raw) : 0;
  const required = Math.max(c, p) > PRICE_CHECK_MIN;
  const limit = refExcl > 0 ? refExcl * (1 + PRICE_CHECK_TOLERANCE) : 0;
  const over = refExcl > 0 && p > limit + 0.005;
  const below = refExcl > 0 && p < refExcl - 0.005;
  const diff = refExcl > 0 ? ((p - refExcl) / refExcl) * 100 : 0;
  const tone = over ? "border-amber-500/60 bg-amber-500/10" : below ? "border-rose-500/60 bg-rose-500/10" : "border-border/60 bg-muted/30";
  return (
    <div className={`rounded-lg border p-2 text-xs space-y-1.5 ${tone}`} data-testid={idPrefix}>
      <div className="flex items-center gap-2 flex-wrap">
        <Globe className="h-3.5 w-3.5 text-primary shrink-0" />
        <Label className="text-[11px] whitespace-nowrap">{t("marketPriceBE")}{required && <span className="text-rose-600"> *</span>}</Label>
        <Input type="number" step="0.01" min="0" className="h-7 text-xs max-w-[110px]" placeholder="0,00" value={reference ?? ""} onChange={(e) => onReference(e.target.value)} data-testid={`${idPrefix}-input`} />
        <div className="inline-flex rounded-md border border-border overflow-hidden text-[10px]" data-testid={`${idPrefix}-vat-toggle`}>
          {[["incl", true], ["excl", false]].map(([k, v]) => (
            <button key={k} type="button" onClick={() => onInclVat?.(v)} className={`px-2 py-1 ${inclVat === v ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground"}`} data-testid={`${idPrefix}-vat-${k}`}>{k}. btw</button>
          ))}
        </div>
        {onSource && (
          <select value={source || "be_webshop"} onChange={(e) => onSource(e.target.value)} className="h-7 rounded-md border border-border bg-background px-1.5 text-[10px]" data-testid={`${idPrefix}-source`}>
            {REF_SOURCES.map((s) => <option key={s} value={s}>{t(`refSource_${s}`)}</option>)}
          </select>
        )}
        {refExcl > 0 && <span className="text-[10px] text-muted-foreground font-mono">= {formatEUR(refExcl)} excl. · {t("window")} {formatEUR(refExcl)}–{formatEUR(limit)}</span>}
      </div>
      {refExcl > 0 && (
        <div className="flex items-center gap-1 font-semibold" data-testid={`${idPrefix}-verdict`}>
          {over ? <><AlertTriangle className="h-3.5 w-3.5 text-amber-700" /><span className="text-amber-700" data-testid={`${idPrefix}-warning`}>+{diff.toFixed(0)} % {t("aboveOnline")} · {t("maxFair")} {formatEUR(limit)} excl. ({formatEUR(limit * VAT)} incl.)</span></>
            : below ? <><TrendingDown className="h-3.5 w-3.5 text-rose-700" /><span className="text-rose-700" data-testid={`${idPrefix}-below`}>{diff.toFixed(0)} % · {t("belowMarket")}</span></>
              : <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-700" /><span className="text-emerald-700" data-testid={`${idPrefix}-ok`}>+{diff.toFixed(0)} % · {t("fairPrice")} — {t("yourPriceIncl")} {formatEUR(p * VAT)}</span></>}
        </div>
      )}
      {source === "nl_webshop" && <p className="text-[10px] text-amber-700" data-testid={`${idPrefix}-nl-hint`}>{t("nlSourceHint")}</p>}
      <p className="text-[10px] text-muted-foreground">{over ? t("priceCheckOverHint") : below ? t("priceCheckBelowHint") : required ? t("priceCheckHint") : t("priceCheckGenericHint")}</p>
    </div>
  );
}
